// React
import { Dispatch, SetStateAction, useEffect, useState } from 'react';

// Refine
import { useTranslation } from '@refinedev/core';

// Mantine
import { Card, Flex, Modal, Stack, Text, Tooltip } from '@mantine/core';
import { UseColorSchemeValue, useDisclosure, useHover } from '@mantine/hooks';
import { MRT_TableInstance } from 'mantine-react-table';

// Interfaces
import { Report } from 'interfaces';

// Custom Components
import { ProviderActivityReportModalContent } from './ProviderActivityReportModalContent';
import { CustomReportModalContent } from './CustomReportModalContent';
import { AccountsReceivableModalContent } from './AccountsReceivableModalContent';
import { TariffProviderReportModalContent } from './TariffProviderReportModalContent';
import { ProviderGroupingReportModalContent } from './ProviderGroupingReportModalContent';
import { CreateRowButton } from 'components/common';
import { isFeatureEnabled } from 'firebase';

// Icons
import {
  IconTrendingUp,
  IconAdjustmentsHorizontal,
  IconFileInvoice,
  IconReportAnalytics,
} from '@tabler/icons-react';

// Hooks
import { useIsProvider, useCanModify, useProviderAllSites } from 'hooks';
import type { Provider, Remittance } from 'interfaces';
import { useCurrentSiteStore } from 'store/SiteStore';
import { useCurrentUserStore } from 'store/UserStore';

interface ReportTopToolbarProps {
  table: MRT_TableInstance<Report>;
  isLoading: boolean;
  computedColorScheme: UseColorSchemeValue;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  providers: Provider[];
  remittances: Remittance[];
}

type ReportType =
  | 'activity'
  | 'custom'
  | 'accountsReceivable'
  | 'tariffProvider'
  | 'grouping';

interface ReportConfigItem {
  type: ReportType;
  title: string;
  description: string;
  icon: React.ReactNode;
  enabled: boolean;
}

interface ReportCardProps {
  config: ReportConfigItem;
  onOpen?: (type: ReportType) => void;
  computedColorScheme?: UseColorSchemeValue;
  table?: MRT_TableInstance<Report>;
  disabled?: boolean;
  isAllSites?: boolean;
}

const ReportCard: React.FC<ReportCardProps> = ({
  config,
  onOpen,
  computedColorScheme,
  disabled,
  isAllSites,
}) => {
  const { hovered, ref } = useHover();
  const { translate } = useTranslation();
  const { icon, title, description, type } = config;
  const showAllSitesTooltip = Boolean(disabled && isAllSites);

  return (
    <Tooltip
      label={translate(
        'pages:reports.allSitesGenerationDisabledTooltip',
        'Select a single site to generate a report.'
      )}
      position="top"
      withArrow
      disabled={!showAllSitesTooltip}
    >
      <div
        data-testid={`report-card-${type}`}
        style={{
          width: '20%',
          cursor: disabled ? 'not-allowed' : undefined,
        }}
      >
        <Card
          ref={ref}
          shadow={hovered ? 'md' : 'sm'}
          padding="lg"
          radius="lg"
          withBorder
          w="100%"
          aria-disabled={disabled}
          onClick={() => {
            if (!disabled && onOpen) {
              onOpen(type);
            }
          }}
          style={{
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.5 : 1,
            pointerEvents: disabled ? 'none' : 'auto',
            transition:
              'transform 150ms ease, box-shadow 150ms ease, background-color 150ms ease, border-color 150ms ease',
            transform: hovered && !disabled ? 'translateY(-2px)' : 'none',
            backgroundColor:
              hovered && !disabled
                ? computedColorScheme === 'dark'
                  ? 'var(--mantine-color-dark-5)'
                  : 'var(--mantine-color-gray-0)'
                : undefined,
            borderColor:
              hovered && !disabled
                ? computedColorScheme === 'dark'
                  ? 'var(--mantine-color-blue-6)'
                  : 'var(--mantine-color-blue-5)'
                : undefined,
          }}
        >
          <Stack align="center" gap={4}>
            {icon}
            <Text fw={500} size="md">
              {title}
            </Text>
            <Text c="dimmed" size="sm" ta="center">
              {description}
            </Text>
          </Stack>
        </Card>
      </div>
    </Tooltip>
  );
};

export const ReportTopToolbar: React.FC<ReportTopToolbarProps> = ({
  computedColorScheme,
  table,
  isLoading,
  setIsLoading,
  providers,
  remittances,
}) => {
  const { translate } = useTranslation();
  const isProvider = useIsProvider();
  const { canModify } = useCanModify('reports');
  const { isAllSites } = useProviderAllSites();
  const { id: siteID } = useCurrentSiteStore();
  const {
    cognitoID,
    providerID,
    providers: currentUserProviders,
  } = useCurrentUserStore();
  const linkedProviders = currentUserProviders ?? [];
  const currentProvider =
    providers.find((provider) => provider.id === providerID) ??
    providers.find((provider) => provider.user?.cognitoID === cognitoID) ??
    linkedProviders.find((provider) => provider.id === providerID) ??
    linkedProviders[0];
  const hasCurrentSiteGroups = Boolean(
    currentProvider?.groups?.some((group) => group.siteID === siteID)
  );

  const [opened, { open, close }] = useDisclosure();
  const [selectedType, setSelectedType] = useState<ReportType | null>(null);

  const REPORTS: ReportConfigItem[] = [
    {
      type: 'activity',
      title: translate('pages:reports.providerActivityReport.title'),
      description: translate(
        'pages:reports.providerActivityReport.description'
      ),
      icon: <IconTrendingUp size={24} />,
      enabled: isFeatureEnabled('report_income'),
    },
    {
      type: 'accountsReceivable',
      title: translate('pages:reports.accountsReceivableReport.title'),
      description: translate(
        'pages:reports.accountsReceivableReport.description'
      ),
      icon: <IconFileInvoice size={24} />,
      enabled: isFeatureEnabled('report_accounts_receivable'),
    },
    {
      type: 'tariffProvider',
      title: translate('pages:reports.tariffProviderReport.title'),
      description: translate('pages:reports.tariffProviderReport.description'),
      icon: <IconReportAnalytics size={24} />,
      enabled: isFeatureEnabled('report_tariff_provider'),
    },
    {
      type: 'grouping',
      title: translate('pages:reports.providerGroupingReport.title'),
      description: translate(
        'pages:reports.providerGroupingReport.description'
      ),
      icon: <IconReportAnalytics size={24} />,
      enabled:
        hasCurrentSiteGroups && isFeatureEnabled('report_provider_grouping'),
    },
    {
      type: 'custom',
      title: translate('pages:reports.customReport.title'),
      description: translate('pages:reports.customReport.description'),
      icon: <IconAdjustmentsHorizontal size={24} />,
      enabled: isFeatureEnabled('report_custom'),
    },
  ];

  const enabledReports = REPORTS.filter((report) => report.enabled);

  useEffect(() => {
    if (selectedType && !enabledReports.some((r) => r.type === selectedType)) {
      setSelectedType(null);
    }
  }, [enabledReports, selectedType]);

  const handleOpen = (type: ReportType) => {
    setSelectedType(type);
    open();
  };

  const modalTitle = selectedType
    ? enabledReports.find((r) => r.type === selectedType)?.title || ''
    : '';

  return (
    <>
      {isProvider ? (
        <>
          <Flex gap="xl" mb={16} w="100%" direction={'row'}>
            {enabledReports.map((r) => (
              <ReportCard
                key={r.type}
                config={r}
                onOpen={handleOpen}
                computedColorScheme={computedColorScheme}
                disabled={isAllSites}
                isAllSites={isAllSites}
              />
            ))}
          </Flex>
          <Modal
            opened={opened}
            onClose={close}
            title={modalTitle}
            // Use a fixed pixel width for custom report to prevent content from expanding modal width
            // Still responsive for smaller screens
            size={selectedType === 'custom' ? 1000 : 'xl'}
            radius="lg"
          >
            {selectedType === 'activity' && (
              <ProviderActivityReportModalContent
                close={close}
                setIsLoading={setIsLoading}
                isLoading={isLoading}
              />
            )}
            {selectedType === 'custom' && (
              <CustomReportModalContent
                close={close}
                setIsLoading={setIsLoading}
              />
            )}
            {selectedType === 'accountsReceivable' && (
              <AccountsReceivableModalContent
                close={close}
                setIsLoading={setIsLoading}
                isLoading={isLoading}
              />
            )}
            {selectedType === 'tariffProvider' && (
              <TariffProviderReportModalContent
                close={close}
                setIsLoading={setIsLoading}
                isLoading={isLoading}
              />
            )}
            {selectedType === 'grouping' && currentProvider && (
              <ProviderGroupingReportModalContent
                provider={currentProvider}
                remittances={remittances}
                close={close}
                setIsLoading={setIsLoading}
                isLoading={isLoading}
              />
            )}
            {!selectedType && <Text>Select a report type.</Text>}
          </Modal>
        </>
      ) : (
        <Flex gap="sm">
          <CreateRowButton
            table={table}
            type="report"
            isLoading={isLoading}
            disabled={!canModify}
          />
        </Flex>
      )}
    </>
  );
};
